import { observer } from 'mobx-react';
import { useInjection } from 'inversify-react';
import { TYPES } from '@/engine/ioc';
import { ImageViewPortWrapper } from '../../views/ImageViewPortWrapper';
import { AccentedQuadroLayout } from './AccentedQuadroLayout';
import { ThreeDViewPortWrapper } from '../../views/GridThreeDViewPortWrapper';
import { PointsSwitcher } from '../../../PointsSwitcher/PointsSwitcher';
import styled from 'styled-components';
import { StepsList } from '../../../stepsList/StepsList';
import { CollapseSidebarButton } from '../../views/collapse-sidebar/CollapseSidebarButton';
import { useEffect } from 'react';
import type { AccentedQuadroViewLayoutController } from './AccentedQuadroViewLayoutController';
import { CineloopHover } from '@/components/loading-animation/CineloopHover';
import { useTheme } from '@mui/material/styles';

export interface ViewPortProps extends React.HTMLAttributes<HTMLDivElement> {
  descriptor: string;
  bgColor: number;
}

export const QUADRO_LAYOUT_PLACEHOLDERS = ['topLeft', 'middleLeft', 'bottomLeft', 'main'] as const;

export const AccentedQuadroViewLayout: React.FC = observer(() => {
  const controller = useInjection<AccentedQuadroViewLayoutController>(TYPES.AccentedQuadroViewLayoutController);
  const theme = useTheme();

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'h' && e.ctrlKey) {
        controller.setSidebarCollapsed(!controller.isSidebarCollapsed);
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handler);

    return (): void => window.removeEventListener('keydown', handler);
  }, [controller]);

  const imagesContent = controller.imageViews.map((iv) => {
    const placeholderDescriptor = controller.getLayoutPlaceholderDescriptorByContentDescriptor(iv.descriptor);
    if (!placeholderDescriptor) return null;
    return {
      placeholderDescriptor: placeholderDescriptor,
      content: (
        <SideContentWrapper style={{ borderColor: iv.borderColor }}>
          <ImageViewPortWrapper
            className="image-viewport-wrapper" // [namazov]: legacy. internal buttons rely on this class
            imageView={iv}
            isMaximized={placeholderDescriptor === 'main'}
            maximize={(): void => {
              controller.maximizeView(placeholderDescriptor);
            }}
            clippingManager={controller.clippingManager}
            key={iv.descriptor}
          />
        </SideContentWrapper>
      ),
      borderColor: iv.borderColor
    };
  });

  const threeDContent = controller.threeDViews.map((td) => {
    const placeholderDescriptor = controller.getLayoutPlaceholderDescriptorByContentDescriptor(td.descriptor);
    if (!placeholderDescriptor) return null;
    return {
      placeholderDescriptor: placeholderDescriptor,
      content: (
        <SideContentWrapper style={{ borderColor: theme.mode === 'dark' ? 'black' : 'white' }}>
          <ThreeDViewPortWrapper
            isMaximized={placeholderDescriptor === 'main'}
            onMaximize={(): void => {
              controller.maximizeView(placeholderDescriptor);
            }}
            key={td.descriptor}
            threeDView={td}
            viewOrientationDescriptor={controller.viewOrientationDescriptor}
            helpers={controller.helpers}
            isCollapsed={false}
          />
        </SideContentWrapper>
      ),
      borderColor: theme.mode === 'dark' ? 'black' : 'white'
    };
  });

  const descriptor2ContentMap: Record<string, { content: React.ReactNode; borderColor: string }> = [
    imagesContent,
    threeDContent
  ]
    .flat()
    .reduce(
      (acc, cur) => {
        if (!cur?.placeholderDescriptor) return acc;
        acc[cur.placeholderDescriptor] = { content: cur.content, borderColor: cur.borderColor };
        return acc;
      },
      {} as Record<string, { content: React.ReactNode; borderColor: string }>
    );

  // TODO: [namazov]: Points switcher is surgery-specific, it shouldn't be here
  const mainContent = (
    <MainContentContainer>
      {descriptor2ContentMap['main'].content}
      <ButtonsContainer>
        <PointsSwitcher />
      </ButtonsContainer>
    </MainContentContainer>
  );

  const footerContent = (
    <StepsContainer data-testid="stepsContainer" className="steps-container">
      <CineloopHover />
      <StepsList />
    </StepsContainer>
  );

  const collapserColor = descriptor2ContentMap['main'].borderColor;

  const collapserContnet = (
    <Collapser $color={collapserColor} data-testid="collapserBar">
      <CollapseSidebarButton
        isCollapsed={controller.isSidebarCollapsed}
        toggleCollapse={(): void => {
          controller.setSidebarCollapsed(!controller.isSidebarCollapsed);
        }}
      />
    </Collapser>
  );

  // [namazov]: originally it was hardcoded to quadro layout, so I left it as is. Consider generalizing it
  return (
    <AccentedQuadroLayout
      isSidebarCollapsed={controller.isSidebarCollapsed}
      topLeftContent={descriptor2ContentMap['topLeft'].content}
      middleLeftContent={descriptor2ContentMap['middleLeft'].content}
      bottomLeftContent={descriptor2ContentMap['bottomLeft'].content}
      mainContent={mainContent}
      footerContent={footerContent}
      collapser={collapserContnet}
    />
  );
});

const ButtonsContainer = styled.div`
  position: relative;
  margin-left: 1.5rem;
  pointer-events: none;
`;

const MainContentContainer = styled.div`
  height: 100%;
`;

const StepsContainer = styled.div`
  position: relative;
  background-color: ${({ theme }): string => theme.customPalette.panels.stepper.background};
  display: flex;
  align-items: center;
  overflow: hidden;
  pointer-events: none;
  min-height: 40px;
`;

const Collapser = styled.div<{ $color: string | undefined }>`
  position: absolute;
  isolation: isolate;
  top: 0;
  right: -10px;
  width: 10px;
  height: 100%;
  z-index: 250;
  background-color: ${({ theme, $color }): string => $color ?? theme.customPalette.panels.collapseImagesBar.background};
  color: ${({ theme, $color }): string =>
    $color !== 'white' ? 'white' : theme.customPalette.panels.collapseImagesBar.font};
  box-shadow: 2px 0 3px rgb(80 90 98 / 50%);
`;

const SideContentWrapper = styled.div`
  height: 100%;
  border-left: 10px solid transparent;
`;
